import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { RoleProvider } from './contexts/RoleContext';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import { Dashboard } from './pages/Dashboard';
import { Leads } from './pages/Leads';
import { LeadDetail } from './pages/LeadDetail';
import { DataSources } from './pages/DataSources';
import { Settings } from './pages/Settings';
import { Admin } from './pages/Admin';
import { Automation } from './pages/Automation';
import { Login } from './pages/Login';
import { useAuth, useDevAuth } from './hooks/useAuth';
import { useRole } from './contexts/RoleContext';
import { AccessDenied } from './components/auth/RoleGuard';
import { Loader2 } from 'lucide-react';

// Use dev auth in development, real auth in production
const useAppAuth = import.meta.env.DEV ? useDevAuth : useAuth;

function AuthenticatedApp() {
  const { user, logout } = useAppAuth();
  const { role, loading: roleLoading } = useRole();

  // Show loading while fetching role
  if (roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-sand">
        <div className="text-center">
          <Loader2 className="inline-block animate-spin h-12 w-12 text-brand-primary" />
          <p className="mt-4 text-font-secondary">Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Show access denied for unauthorized users
  if (role === 'unauthorized') {
    return (
      <div className="min-h-screen bg-brand-sand">
        <Header userName={user?.name} userPicture={user?.picture} onLogout={logout} />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <AccessDenied message="You do not have access to this application. Please contact your administrator." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-sand">
      <Header userName={user?.name} userPicture={user?.picture} onLogout={logout} />

      <div className="flex">
        <Sidebar />

        <main className="flex-1 ml-64 p-6">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:id" element={<LeadDetail />} />
            <Route path="/sources" element={<DataSources />} />
            <Route path="/automation" element={<Automation />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AppContent() {
  const { loading, isAuthenticated, error } = useAppAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-sand">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-brand-primary border-r-transparent" />
          <p className="mt-4 text-font-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login error={error} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <RoleProvider>
      <AuthenticatedApp />
    </RoleProvider>
  );
}

function App() {
  // In dev mode, we don't need the AuthProvider since useDevAuth manages its own state
  if (import.meta.env.DEV) {
    return <AppContent />;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
